/**
 * Canonical per-case conformance results. Evaluation is supplied by the host:
 * Jira's configured happy-path matcher and Process Advisor's discovery-rule
 * evaluator remain authoritative until those evaluators are migrated.
 *
 * An unconfigured metric is NULL, never a manufactured zero or 100%.
 * Evaluations are ephemeral derived state; the runtime re-promotes them after
 * the host runs the current configuration on every modelMetrics pass.
 */
export function enrichCanonicalConformanceDataset(dataset, {
  evaluations = [], workflowConfigured = false, complianceConfigured = false,
  source = 'pa-runtime-evaluation',
} = {}) {
  if (!dataset) return dataset;
  const byId = new Map();
  const byKey = new Map();
  for (const e of evaluations) {
    if (e?.caseId != null && String(e.caseId)) byId.set(String(e.caseId), e);
    if (e?.nativeId != null && String(e.nativeId)) byId.set(String(e.nativeId), e);
    if (e?.caseKey != null && String(e.caseKey)) byKey.set(String(e.caseKey), e);
  }
  const cases = (dataset.cases || []).map(c => {
    const e = byId.get(String(c.id)) || byId.get(String(c?.source?.nativeId || ''))
      || byKey.get(String(c.key)) || byKey.get(String(c?.source?.nativeKey || ''));
    const workflowAlignment = workflowConfigured && typeof e?.workflowAlignment === 'boolean'
      ? e.workflowAlignment : null;
    const compliant = complianceConfigured && typeof e?.compliant === 'boolean'
      ? e.compliant : null;
    return {
      ...c,
      extensions: {
        ...(c.extensions || {}),
        conformance: {
          workflowAlignment, compliant,
          workflowEvaluated: workflowAlignment !== null,
          complianceEvaluated: compliant !== null,
          ...(Array.isArray(e?.violatedRuleIds) ? { violatedRuleIds: [...e.violatedRuleIds] } : {}),
          evaluationSource: source,
        },
      },
    };
  });
  return {
    ...dataset, cases,
    metadata: {
      ...(dataset.metadata || {}),
      conformance: { workflowConfigured: Boolean(workflowConfigured),
        complianceConfigured: Boolean(complianceConfigured), evaluationSource: source },
    },
  };
}

export function deriveCanonicalConformanceSummary(dataset) {
  const cases = dataset?.cases || [];
  const workflow = cases.map(c => c?.extensions?.conformance?.workflowAlignment)
    .filter(value => typeof value === 'boolean');
  const compliance = cases.map(c => c?.extensions?.conformance?.compliant)
    .filter(value => typeof value === 'boolean');
  // An incomplete projection must never silently change the denominator.
  const workflowAvailable = cases.length > 0 && workflow.length === cases.length;
  const complianceAvailable = cases.length > 0 && compliance.length === cases.length;
  return {
    workflowAlignment: workflowAvailable ? workflow.filter(Boolean).length / cases.length : null,
    compliance: complianceAvailable ? compliance.filter(Boolean).length / cases.length : null,
    workflowEvaluatedCount: workflow.length,
    complianceEvaluatedCount: compliance.length,
    caseCount: cases.length,
    evaluationSource: dataset?.metadata?.conformance?.evaluationSource || null,
  };
}
